import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  fetchMerchandisingRule,
  exportMerchandisingCsv,
  importMerchandisingCsv,
  previewMerchandisingCsv,
  saveMerchandisingRule,
  validateMerchandisingRule,
} from '../state/merchandisingSlice';
import {
  createRuleExportManifest,
  parseRuleImport,
  runWithConcurrency,
  serializeRuleExportManifest,
} from '../model/rulePortability';
import { toRuleWriteDto } from '../model/ruleDraft';
import { useLocalizedMessage } from '../../../util/useLocalizedMessage';

const uuid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const value = Math.floor(Math.random() * 16);
    return (char === 'x' ? value : ((value & 3) | 8)).toString(16);
  });
};

const downloadJson = (name, value) => {
  const url = URL.createObjectURL(new Blob([value], { type: 'application/json' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
};

const downloadCsv = (name, value) => {
  const url = URL.createObjectURL(new Blob([value], { type: 'text/csv;charset=utf-8' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
};

const copyName = (name) => {
  const suffix = ' (copy)';
  return `${String(name || 'Rule').slice(0, 160 - suffix.length)}${suffix}`;
};

const copiedExplicitName = (name) => {
  const normalized = String(name || '').trim();
  if (!normalized || ['product ranking rule', 'query override rule'].includes(normalized.toLowerCase())) {
    return null;
  }
  return copyName(normalized);
};

const RuleTransferControls = ({
  selectedRules, onComplete, actions = ['duplicate', 'export', 'import'],
}) => {
  const dispatch = useDispatch();
  const csvEnabled = useSelector((state) => Boolean(state.merchandising?.capability?.csv));
  const t = useLocalizedMessage();
  const inputRef = useRef(null);
  const mutationIdsRef = useRef(new Map());
  const [dialog, setDialog] = useState(null);
  const [working, setWorking] = useState(false);

  const exportRules = async () => {
    setWorking(true);
    const loaded = await runWithConcurrency(selectedRules, async (rule) => {
      const action = await dispatch(fetchMerchandisingRule({ id: String(rule.id) }));
      if (!fetchMerchandisingRule.fulfilled.match(action)) {
        const error = new Error(action.payload?.message || 'Canonical rule load failed');
        error.code = action.payload?.code || 'RULE_LOAD_FAILED';
        throw error;
      }
      return action.payload;
    }, { concurrency: 3 });
    const rules = loaded
      .filter((row) => row.status === 'succeeded')
      .map((row) => row.value);
    const failures = loaded
      .map((row, index) => ({ row, rule: selectedRules[index] }))
      .filter(({ row }) => row.status !== 'succeeded')
      .map(({ row, rule }) => ({
        id: String(rule.id),
        code: row.error?.code || 'RULE_LOAD_FAILED',
      }));
    const manifest = createRuleExportManifest({ rules, failures });
    downloadJson('kea-merchandising-rules.json', serializeRuleExportManifest(manifest));
    setWorking(false);
    if (failures.length > 0) {
      setDialog({
        kind: 'export-result',
        successful: rules.length,
        failed: failures.length,
      });
    }
  };

  const exportCsv = async () => {
    setWorking(true);
    const action = await dispatch(exportMerchandisingCsv({
      type: 'product_ranking',
      ids: selectedRules.map((rule) => String(rule.id)),
    }));
    setWorking(false);
    if (!exportMerchandisingCsv.fulfilled.match(action)) {
      setDialog({ kind: 'error', message: action.payload?.message || 'CSV export failed.' });
      return;
    }
    downloadCsv(action.payload.file_name, action.payload.content);
    if (action.payload.skipped > 0) {
      setDialog({
        kind: 'csv-export-result',
        successful: action.payload.exported,
        failed: action.payload.skipped,
        failures: action.payload.errors,
      });
    }
  };

  const validateRows = async (rules) => runWithConcurrency(rules, async (rule, index) => {
    const action = await dispatch(validateMerchandisingRule({ rule }));
    if (!validateMerchandisingRule.fulfilled.match(action) || !action.payload.valid) {
      const firstDetail = action.payload?.details?.[0];
      const error = new Error(
        action.payload?.message || firstDetail?.message || firstDetail?.reason || 'Validation failed',
      );
      error.code = action.payload?.code || 'INVALID_RULE';
      error.details = action.payload?.details || [];
      throw error;
    }
    const canonical = toRuleWriteDto(action.payload.rule || rule);
    if (!Object.prototype.hasOwnProperty.call(rule, 'name') || !String(rule.name || '').trim()) {
      delete canonical.name;
    }
    return {
      index,
      input: rule,
      canonical,
      warnings: action.payload.warnings || [],
    };
  }, { concurrency: 3 });

  const importFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setWorking(true);
    try {
      const text = await file.text();
      if (/\.csv$/i.test(file.name) || file.type.includes('csv')) {
        if (!csvEnabled) throw new Error('CSV import requires a newer plugin version.');
        const action = await dispatch(previewMerchandisingCsv({
          type: 'product_ranking',
          csv: text,
        }));
        if (!previewMerchandisingCsv.fulfilled.match(action)) {
          throw new Error(action.payload?.message || 'CSV preview failed.');
        }
        setDialog({ kind: 'csv-import', preview: action.payload, csv: text, mutationId: uuid() });
        return;
      }
      const rules = parseRuleImport(text);
      const validation = await validateRows(rules);
      setDialog({ kind: 'import', validation, rules });
    } catch (error) {
      setDialog({ kind: 'error', message: error.message });
    } finally {
      setWorking(false);
    }
  };

  const applyCsv = async () => {
    setWorking(true);
    const action = await dispatch(importMerchandisingCsv({
      type: 'product_ranking',
      csv: dialog.csv,
      clientMutationId: dialog.mutationId,
    }));
    setWorking(false);
    if (!importMerchandisingCsv.fulfilled.match(action)) {
      setDialog({ kind: 'error', message: action.payload?.message || 'CSV import failed.' });
      return;
    }
    setDialog({ kind: 'csv-result', successful: action.payload.created, failed: 0 });
    if (onComplete) onComplete();
  };

  const createRows = async (validRows) => {
    setWorking(true);
    const results = await runWithConcurrency(validRows, async (row) => {
      const fingerprint = JSON.stringify(row.canonical);
      const mutationKey = `${dialog.sourceKind || dialog.kind}:${row.index}:${fingerprint}`;
      if (!mutationIdsRef.current.has(mutationKey)) mutationIdsRef.current.set(mutationKey, uuid());
      const action = await dispatch(saveMerchandisingRule({
        rule: row.canonical,
        clientMutationId: mutationIdsRef.current.get(mutationKey),
      }));
      if (!saveMerchandisingRule.fulfilled.match(action)) {
        const error = new Error(action.payload?.message || 'Create failed');
        error.code = action.payload?.code || 'RULE_CREATE_FAILED';
        error.details = action.payload?.details || [];
        throw error;
      }
      return action.payload.rule || action.payload;
    }, { concurrency: 3 });
    setWorking(false);
    const retryRows = results
      .map((result, index) => ({ result, row: validRows[index] }))
      .filter(({ result }) => result.status !== 'succeeded')
      .map(({ row }) => row);
    const failures = results
      .map((result, index) => ({ result, row: validRows[index] }))
      .filter(({ result }) => result.status !== 'succeeded')
      .map(({ result, row }, index) => ({
        name: row?.canonical?.name || row?.input?.name || t('merchandising.transfer.unnamedRule', { number: index + 1 }),
        code: result.error?.code || 'RULE_CREATE_FAILED',
        message: result.error?.message || t('merchandising.transfer.createFailed'),
      }));
    setDialog({
      kind: 'result',
      results,
      retryRows,
      failures,
      sourceKind: dialog.kind === 'result' ? dialog.sourceKind : dialog.kind,
    });
    if (onComplete) onComplete();
  };
  const createValidated = async () => {
    const validRows = dialog.validation
      .filter((item) => item?.status === 'succeeded')
      .map((item) => item.value);
    await createRows(validRows);
  };

  const duplicate = async () => {
    setWorking(true);
    const drafts = selectedRules.map((rule) => {
      const draft = toRuleWriteDto(rule);
      const name = copiedExplicitName(rule.name);
      if (name) draft.name = name;
      else delete draft.name;
      return draft;
    });
    const validation = await validateRows(drafts);
    setWorking(false);
    setDialog({ kind: 'duplicate', validation, rules: drafts });
  };

  const successful = dialog?.validation?.filter((row) => row?.status === 'succeeded').length || 0;
  const failed = dialog?.validation?.filter((row) => row?.status === 'failed').length || 0;
  const validationFailures = dialog?.validation?.map((result, index) => ({
    result,
    rule: dialog.rules?.[index],
  })).filter(({ result }) => result?.status === 'failed').map(({ result, rule }, index) => ({
    name: rule?.name || t('merchandising.transfer.unnamedRule', { number: index + 1 }),
    code: result.error?.code || 'INVALID_RULE',
    message: result.error?.message || t('merchandising.transfer.validationFailed'),
  })) || [];
  const dialogTitle = dialog?.kind === 'duplicate'
    ? t('merchandising.transfer.duplicateTitle')
    : (['import', 'csv-import'].includes(dialog?.kind) ? t('merchandising.transfer.importTitle')
      : (['export-result', 'csv-export-result'].includes(dialog?.kind) ? t('merchandising.transfer.exportResultTitle')
        : (dialog?.sourceKind === 'duplicate'
          ? t('merchandising.transfer.duplicateResultTitle')
          : t('merchandising.transfer.importResultTitle'))));

  const renderFailures = (failures) => failures.length > 0 && (
    <div className="mp-transfer-failures" role="list">
      {failures.map((failure, index) => (
        <div key={`${failure.name}-${failure.code}-${index}`} role="listitem">
          <strong>{failure.name}</strong>
          <span>{failure.message}</span>
          <code>{failure.code}</code>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <Box display="flex" className="mp-transfer-actions" style={{ gap: 8 }}>
        {actions.includes('duplicate') && <Button
          size="small"
          disabled={working || selectedRules.length === 0 || selectedRules.length > 100}
          onClick={duplicate}
        >
          {t('merchandising.transfer.duplicate')}
        </Button>}
        {actions.includes('export') && <Button
          size="small"
          disabled={working || selectedRules.length === 0 || selectedRules.length > 100}
          onClick={exportRules}
        >
          {t('merchandising.transfer.exportJson')}
        </Button>}
        {actions.includes('export') && csvEnabled && <Button
          size="small"
          disabled={working || selectedRules.length === 0 || selectedRules.length > 100}
          onClick={exportCsv}
        >
          {t('merchandising.transfer.exportCsv')}
        </Button>}
        {actions.includes('import') && <Button size="small" disabled={working} onClick={() => inputRef.current.click()}>
          {t('merchandising.transfer.import')}
        </Button>}
        {actions.includes('import') && <input
          ref={inputRef}
          hidden
          type="file"
          accept={csvEnabled ? 'application/json,text/csv,.json,.csv' : 'application/json,.json'}
          onChange={importFile}
          aria-label={t('merchandising.transfer.import')}
        />}
      </Box>
      <Dialog
        className="mp-dialog"
        open={Boolean(dialog)}
        onClose={() => !working && setDialog(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          {dialog?.kind === 'error' && <Alert severity="error">{dialog.message}</Alert>}
          {(dialog?.kind === 'import' || dialog?.kind === 'duplicate') && (
            <>
              <Typography>
                {t('merchandising.transfer.validationSummary', { successful, failed })}
              </Typography>
              {failed > 0 && (
                <Alert severity="warning">{t('merchandising.transfer.invalidExcluded')}</Alert>
              )}
              {renderFailures(validationFailures)}
              <Typography variant="body2" color="textSecondary">
                {t('merchandising.transfer.draftOnly')}
              </Typography>
            </>
          )}
          {dialog?.kind === 'csv-import' && (
            <>
              <Typography>
                {t('merchandising.transfer.csvPreview', {
                  rules: dialog.preview.item_count,
                  rows: dialog.preview.row_count,
                })}
              </Typography>
              {!dialog.preview.valid && (
                <Alert severity="error">{t('merchandising.transfer.csvInvalid')}</Alert>
              )}
              {renderFailures((dialog.preview.errors || []).map((error) => ({
                name: error.row
                  ? `${t('merchandising.transfer.csvRow', { row: error.row })}${error.column ? ` · ${error.column}` : ''}`
                  : t('merchandising.transfer.csvFile'),
                code: error.code,
                message: error.message,
              })))}
              <Typography variant="body2" color="textSecondary">
                {t('merchandising.transfer.draftOnly')}
              </Typography>
            </>
          )}
          {dialog?.kind === 'result' && (
            <>
              <Typography>
                {t('merchandising.transfer.result', {
                successful: dialog.results.filter((row) => row.status === 'succeeded').length,
                failed: dialog.results.filter((row) => row.status !== 'succeeded').length,
                })}
              </Typography>
              {renderFailures(dialog.failures || [])}
            </>
          )}
          {dialog?.kind === 'csv-result' && (
            <Typography>
              {t('merchandising.transfer.result', {
                successful: dialog.successful,
                failed: dialog.failed,
              })}
            </Typography>
          )}
          {['export-result', 'csv-export-result'].includes(dialog?.kind) && (
            <Alert severity="warning">
              {t('merchandising.transfer.exportPartial', {
                successful: dialog.successful,
                failed: dialog.failed,
              })}
            </Alert>
          )}
          {dialog?.kind === 'csv-export-result' && renderFailures((dialog.failures || []).map((error) => ({
            name: error.id ? `Rule ${error.id}` : 'Rule',
            code: error.code,
            message: error.message,
          })))}
        </DialogContent>
        <DialogActions className="mp-dialog-actions">
          <Button variant="outlined" disabled={working} onClick={() => setDialog(null)}>
            {t('merchandising.transfer.close')}
          </Button>
          {(dialog?.kind === 'import' || dialog?.kind === 'duplicate') && successful > 0 && (
            <Button color="primary" variant="contained" disabled={working} onClick={createValidated}>
              {t('merchandising.transfer.createDrafts', { count: successful })}
            </Button>
          )}
          {dialog?.kind === 'csv-import' && dialog.preview.valid && (
            <Button color="primary" variant="contained" disabled={working} onClick={applyCsv}>
              {t('merchandising.transfer.createDrafts', { count: dialog.preview.item_count })}
            </Button>
          )}
          {dialog?.kind === 'result' && dialog.retryRows.length > 0 && (
            <Button
              color="primary"
              variant="contained"
              disabled={working}
              onClick={() => createRows(dialog.retryRows)}
            >
              {t('merchandising.transfer.retryFailed', { count: dialog.retryRows.length })}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

RuleTransferControls.propTypes = {
  selectedRules: PropTypes.arrayOf(PropTypes.object).isRequired,
  onComplete: PropTypes.func,
  actions: PropTypes.arrayOf(PropTypes.oneOf(['duplicate', 'export', 'import'])),
};

RuleTransferControls.defaultProps = { onComplete: null, actions: undefined };

export default RuleTransferControls;
